import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { pagesText } from '../../../../constants/pagesText';
import DeleteButton from '../../../../components/Filters/DeleteButton';
import Scrollbar from '../../../../components/Scrollbar';
import ActiveFilterSection from '../../../../components/Filters/ActiveFilterSection';
import SidebarListItem from '../../../../components/Filters/SidebarListItem';
import SidebarFooter from '../../../../components/Filters/SidebarFooter';
import { defaultCatalogInventoryFilters } from '../../../../components/Filters/constants';
import { catalogInventorySidebarList } from './constants';
import TagService from '../../../../services/tag.service';
import useSubscribeStorage from '../../../../hooks/useSubscribeStorage';

const FilterSidebarTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontWeight: 500,
  fontSize: theme.typography.pxToRem(18),
  lineHeight: '24px',
  marginLeft: theme.spacing(3.5),
}));

const SidebarDivider = () => (
  <Divider
    ornentation="horizontal"
    sx={{
      backgroundColor: '#2C2C2C',
    }}
  />
);

const WdacCatalogInventorySidebar = ({
  open,
  onClose,
  onApplyFilters,
  initialQueryFilters,
  query,
}) => {
  const [activeFilterSection, setActiveFilterSection] = useState(null);
  const customerEnvId = useSubscribeStorage('defaultEnvironmentId');

  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(query);
    const isSignedFromQuery = params.get('Filter.IsSigned');

    let isSignedFilter = defaultCatalogInventoryFilters.IsSigned;
    if (isSignedFromQuery !== null) {
      isSignedFilter = [
        [{ ...defaultCatalogInventoryFilters.IsSigned[0][0], value: isSignedFromQuery }],
      ];
    }

    return {
      FilterCatalogInventoryResult:
        initialQueryFilters?.FilterCatalogInventoryResult ||
        defaultCatalogInventoryFilters.FilterCatalogInventoryResult,
      Tags: initialQueryFilters?.Tags || defaultCatalogInventoryFilters.Tags,
      Filter: initialQueryFilters?.Filter || defaultCatalogInventoryFilters.Filter,
      IsSigned: initialQueryFilters?.IsSigned || isSignedFilter,
    };
  }, [initialQueryFilters, query]);

  const [selectedFilters, setSelectedFilters] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (open) {
      const params = new URLSearchParams(query);
      const isSignedFromQuery = params.get('Filter.IsSigned');

      if (isSignedFromQuery !== null) {
        const updatedIsSigned = [
          [{ ...defaultCatalogInventoryFilters.IsSigned[0][0], value: isSignedFromQuery }],
        ];
        setSelectedFilters(prev => ({
          ...prev,
          IsSigned: updatedIsSigned,
        }));
        setFilters(prev => ({
          ...prev,
          IsSigned: updatedIsSigned,
        }));
      } else {
        setSelectedFilters(prev => ({
          ...prev,
          IsSigned: defaultCatalogInventoryFilters.IsSigned,
        }));
        setFilters(prev => ({
          ...prev,
          IsSigned: defaultCatalogInventoryFilters.IsSigned,
        }));
      }
    }
  }, [open, query]);

  const onChangeActiveSidebar = useCallback(
    activeSection => {
      setActiveFilterSection(activeSection);
    },
    [setActiveFilterSection],
  );

  const onDrawerClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      setActiveFilterSection(null);
      setSelectedFilters(filters);
    }, 300);
  }, [onClose, setActiveFilterSection, setSelectedFilters, filters]);

  const onChangeFilterValue = useCallback(
    ({ source, fieldIndex, sectionIndex, fieldValue, label }) => {
      setSelectedFilters(prevState => {
        const newSourceData = [...prevState[source]];
        newSourceData[sectionIndex] = newSourceData[sectionIndex].map((filterField, index) => {
          if (index === fieldIndex) {
            return {
              ...filterField,
              value: fieldValue,
            };
          }

          return filterField;
        });

        return {
          ...prevState,
          [source]: newSourceData,
        };
      });
    },
    [],
  );

  const onAddFilter = useCallback(() => {
    const selectedFilterField = activeFilterSection.name;
    setSelectedFilters(prevState => ({
      ...prevState,
      [selectedFilterField]: [
        ...prevState[selectedFilterField],
        ...defaultCatalogInventoryFilters[selectedFilterField],
      ],
    }));
  }, [activeFilterSection?.name, setSelectedFilters]);

  const onRemoveFilter = useCallback(
    sectionIndex => {
      const selectedFilterField = activeFilterSection.name;
      setSelectedFilters(prevState => ({
        ...prevState,
        [selectedFilterField]: prevState[selectedFilterField].filter(
          (filter, filterIndex) => filterIndex !== sectionIndex,
        ),
      }));
    },
    [activeFilterSection?.name, setSelectedFilters],
  );

  const resetFilters = useCallback(() => {
    if (!activeFilterSection) {
      setFilters(defaultCatalogInventoryFilters);
      setSelectedFilters(defaultCatalogInventoryFilters);
      return;
    }

    setFilters(prevState => ({
      ...prevState,
      [activeFilterSection.name]: defaultCatalogInventoryFilters[activeFilterSection.name],
    }));
    setSelectedFilters(prevState => ({
      ...prevState,
      [activeFilterSection.name]: defaultCatalogInventoryFilters[activeFilterSection.name],
    }));
    setActiveFilterSection(null);
  }, [activeFilterSection]);

  const onSuccess = useCallback(() => {
    if (!!activeFilterSection) {
      setFilters(prevState => ({
        ...prevState,
        [activeFilterSection.name]: selectedFilters[activeFilterSection.name],
      }));
      setActiveFilterSection(null);
      return;
    }

    // Normalize filter values before applying - convert 'none' and 'all' to empty string
    const normalizedFilters = { ...selectedFilters };
    if (normalizedFilters.IsSigned) {
      const isSignedValue = normalizedFilters.IsSigned[0]?.[0]?.value;
      if (isSignedValue === 'none' || isSignedValue === 'all') {
        normalizedFilters.IsSigned = [[{ ...normalizedFilters.IsSigned[0][0], value: '' }]];
      }
    }

    onApplyFilters(normalizedFilters, 'apply');
    onDrawerClose();
  }, [activeFilterSection, onApplyFilters, selectedFilters, onDrawerClose]);

  const onBackClick = useCallback(() => {
    setActiveFilterSection(null);
  }, [setActiveFilterSection]);

  const isApplyFiltersDisabled = useMemo(() => {
    if (!activeFilterSection) return false;

    const activeFilterSectionFilters = selectedFilters[activeFilterSection.name];
    if (!activeFilterSectionFilters) return true;

    return activeFilterSectionFilters.flat().every(filter => filter.value === '');
  }, [activeFilterSection, selectedFilters]);

  const checkIsFilterListItemActive = useCallback(
    name => filters[name].flat().some(item => item.value !== ''),
    [filters],
  );

  const fetchFunctionsMap = useMemo(
    () => ({
      Tags: params =>
        TagService.getTags({
          ...params,
          customerEnvId,
        }),
    }),
    [customerEnvId],
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onDrawerClose}
      PaperProps={{
        sx: theme => ({
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(6, 0),
          width: '100%',
          maxWidth: '344px',
          backgroundImage: 'unset',
        }),
      }}>
      <Box position="relative" height="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 1.5,
          }}>
          <FilterSidebarTitle>Filter By Column</FilterSidebarTitle>
          <DeleteButton
            onClick={resetFilters}
            hasIcon={false}
            styles={{
              marginRight: '28px',
            }}>
            {pagesText.filters.resetFiltersButton}
          </DeleteButton>
        </Stack>
        <SidebarDivider />
        <Scrollbar
          height="calc(100% - 80px)"
          styles={{
            padding: '0 22px',
          }}>
          {activeFilterSection ? (
            <ActiveFilterSection
              title={activeFilterSection.title}
              source={activeFilterSection.name}
              filters={selectedFilters[activeFilterSection.name]}
              onChangeFilterValue={onChangeFilterValue}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onBackClick={onBackClick}
              fetchFunctionsMap={activeFilterSection?.options?.length ? null : fetchFunctionsMap}
              options={activeFilterSection?.options}
            />
          ) : (
            <Box display="flex" mt={2.5} flexDirection="column" gap={2.25}>
              {catalogInventorySidebarList.map(
                ({ title, name, options = [], useCheckbox = false }) => (
                  <SidebarListItem
                    name={name}
                    key={name}
                    title={title}
                    options={options}
                    query={query}
                    filters={selectedFilters}
                    useCheckbox={useCheckbox}
                    isActive={checkIsFilterListItemActive(name)}
                    onChangeFilterValue={onChangeFilterValue}
                    onClick={() => onChangeActiveSidebar({ title, name, options })}
                  />
                ),
              )}
            </Box>
          )}
        </Scrollbar>
        <SidebarFooter
          onClose={onDrawerClose}
          onSuccess={onSuccess}
          isApplyFiltersDisabled={isApplyFiltersDisabled}
          isActiveFilterSection={!!activeFilterSection?.name}
        />
      </Box>
    </Drawer>
  );
};

export default memo(WdacCatalogInventorySidebar);
